import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Loader2, Receipt, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { API } from '../utils/apiConfig';

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-slate-200 text-slate-700',
};

const err = (e) => e?.response?.data?.detail || e?.message || 'Error';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })} ${d.getFullYear()}`;
  } catch { return iso.slice(0, 10); }
};

export default function PublicExpenseApprovalPage() {
  const { requestId } = useParams();
  const [sp] = useSearchParams();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobile, setMobile] = useState(sp.get('m') || '');
  const [remarks, setRemarks] = useState('');
  const [acting, setActing] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/expenses/public/${requestId}`);
      setView(r.data);
    } catch (e) { toast.error(err(e)); }
    finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    const clean = mobile.replace(/\D/g, '');
    if (clean.length < 10) { toast.error('Enter your registered mobile number'); return; }
    if (action === 'rejected' && !remarks.trim()) {
      if (!window.confirm('Reject without a reason?')) return;
    }
    setActing(action);
    try {
      const r = await axios.post(`${API}/expenses/public/${requestId}/action`, {
        mobile: clean, action, remarks,
      });
      setView(r.data.view);
      setDone(action);
      toast.success(action === 'approved' ? 'Vote recorded — Approved' : 'Vote recorded — Rejected');
    } catch (e) { toast.error(err(e)); }
    finally { setActing(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (!view) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="p-6 max-w-sm text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
          <p>Request not found or link has expired.</p>
        </Card>
      </div>
    );
  }

  const canVote = ['pending', 'approved'].includes(view.status) && !view.public_link_expired && !done;

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="max-w-md mx-auto">
        <header className="text-center mb-4">
          <div className="inline-flex items-center gap-2 text-emerald-700 mb-1">
            <Receipt className="w-6 h-6" />
            <h1 className="text-lg font-semibold">MAKS · Expense Approval</h1>
          </div>
          <p className="text-xs text-slate-500">You have been asked to approve or reject this expense.</p>
        </header>

        <Card className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-emerald-700 text-sm" data-testid="pub-request-no">{view.request_no}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[view.status] || ''}`} data-testid="pub-status">{view.status}</span>
          </div>

          <div className="space-y-1 text-sm">
            <Row label="Venue" value={view.venue} />
            <Row label="Expense Type" value={view.expense_type} />
            <Row label="Amount" value={inr(view.amount)} className="font-semibold text-base" />
            {view.description && <Row label="Description" value={view.description} />}
            <Row label="Requester" value={view.requester_name} />
            <Row label="Created" value={fmtDate(view.created_at)} />
            <Row label="Deadline" value={fmtDate(view.deadline_at)} />
            <Row label="Approvals" value={`${view.approve_count}/${view.min_approvals_required}`} />
          </div>

          <div className="pt-3 border-t">
            <p className="text-xs font-medium mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Voting</p>
            <div className="space-y-1 text-xs" data-testid="pub-votes">
              {(view.votes || []).map((v, i) => {
                const c = v.action === 'approved' ? 'text-emerald-700' : v.action === 'rejected' ? 'text-red-700' : 'text-slate-500';
                return (
                  <div key={i} className={c}>
                    <span className="mr-2">{v.action === 'approved' ? '✓' : v.action === 'rejected' ? '✗' : '•'}</span>
                    <span className="font-medium">{v.name}</span>
                    <span className="text-slate-400"> · {v.mobile_masked}</span>
                    <span> — {v.action}{v.timestamp && ` on ${fmtDate(v.timestamp)}`}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {canVote ? (
            <div className="pt-3 border-t space-y-2">
              <div>
                <Label className="text-xs">Your Registered Mobile Number</Label>
                <Input inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit mobile" data-testid="pub-mobile" />
              </div>
              <div>
                <Label className="text-xs">Remarks (optional)</Label>
                <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Add a note (optional)" data-testid="pub-remarks" />
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700 text-base py-6"
                  onClick={() => act('approved')} disabled={!!acting}
                  data-testid="pub-approve-btn"
                >
                  {acting === 'approved' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-1" /> Approve</>}
                </Button>
                <Button
                  variant="destructive" className="text-base py-6"
                  onClick={() => act('rejected')} disabled={!!acting}
                  data-testid="pub-reject-btn"
                >
                  {acting === 'rejected' ? <Loader2 className="w-5 h-5 animate-spin" /> : <><XCircle className="w-5 h-5 mr-1" /> Reject</>}
                </Button>
              </div>
              <p className="text-[10px] text-slate-400 text-center pt-1">One response per registered mobile. Requester cannot vote.</p>
            </div>
          ) : (
            <div className="pt-3 border-t text-center text-xs text-slate-500" data-testid="pub-closed">
              {done ? (
                <p className="text-emerald-700">Thanks, your vote has been recorded.</p>
              ) : view.public_link_expired && view.status === 'pending' ? (
                <p>This approval link has expired.</p>
              ) : (
                <p>Voting is closed. Final status: <b>{view.status}</b>.</p>
              )}
            </div>
          )}
        </Card>

        <p className="text-center text-[10px] text-slate-400 mt-3">
          Powered by MAKS Pickleball · <a href="https://play.makspickleball.com/" className="underline">Open Admin</a>
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, className = '' }) {
  return (
    <div className="flex items-baseline">
      <span className="text-xs text-slate-500 w-24 shrink-0">{label}</span>
      <span className={`ml-2 ${className}`}>{value}</span>
    </div>
  );
}
